<script setup lang="ts">
import {useConfirm} from "primevue/useconfirm";
import {useSafeDelete} from "@/Composables/useSafeFormAction";
import {Button} from "@/Components/Library";
import axios from "axios";
import {useToast} from "primevue/usetoast";

const toast = useToast();

type Props = {
    name: string;
    header?: string;
    message?: string;
    deleted?: boolean;
    routes: {
        show?: {name: string; params?: Record<string, string | number>};
        edit?: {name: string; params?: Record<string, string | number>};
        delete?: string;
        restore?: string;
    };
};

const props = defineProps<Props>();
const confirm = useConfirm();
const emit = defineEmits(["deleted"]);

const handleDelete = ($event: Event) =>
    useSafeDelete({
        header: props.header,
        message: props.message,
        event: $event,
        confirm,
        acceptRoute: props.routes.delete ?? "",
        onSuccess: () => {
            emit("deleted");
            toast.add({
                severity: "success",
                summary: "Success",
                detail: `${props.name} archived successfully`,
                life: 3000,
            });
        },
    });

const handleRestore = (event: Event) => {
    confirm.require({
        message: `Are you sure you want to restore ${props.name}?`,
        header: "Restore",
        target: event.target as HTMLElement,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes",
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectLabel: "No",
        rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        accept: async () => {
            await axios.post(props.routes.restore);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: `${props.name} restored successfully`,
                life: 3000,
            });
            emit("deleted");
        },
    });
};
</script>
<template>
    <div class="relative flex items-center gap-1">
        <RouterLink v-if="routes.show" :to="routes.show" class="focus-visible:outline-none">
            <Button
                v-tooltip.left="`View ${name}`"
                rounded
                class="h-8 w-8 bg-blue-500 text-white"
                :icon="{
                    icon: 'clarity:eye-solid',
                    width: '16',
                }"
            />
        </RouterLink>
        <RouterLink v-if="routes.edit" :to="routes.edit" class="focus-visible:outline-none">
            <Button
                v-tooltip.left="`Edit ${name}`"
                rounded
                class="h-8 w-8 bg-amber-500 text-white"
                :icon="{
                    icon: 'clarity:edit-solid',
                    width: '16',
                }"
            />
        </RouterLink>
        <!--        TODO: Might be a good idea to consider adding either "Don't ask for confirmation for X minutes" feature and/or a mass selection for deletion -->
        <Button
            v-if="routes.delete && !deleted"
            v-tooltip.left="`Archive ${name}`"
            rounded
            :icon="{
                icon: 'clarity:trash-solid',
                width: '16',
            }"
            class="h-8 w-8 bg-red-600 text-white"
            @click="handleDelete($event)"
        />
        <Button
            v-if="routes.restore && deleted"
            v-tooltip.left="`Restore ${name}`"
            rounded
            :icon="{
                icon: 'clarity:backup-restore-solid',
                width: '16',
            }"
            class="h-8 w-8 bg-green-600 text-white"
            @click="handleRestore($event)"
        />
        <slot />
    </div>
</template>
